import { useTown } from "@/hooks/use-town";
import { orderingPaymentAppearance } from "@/lib/orderingAppearance";
import { useState, useEffect, useRef, useMemo } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Loader2, ShoppingBag, Check, AlertTriangle, ChevronDown, ChevronUp, Clock } from "lucide-react";

const ADDRESSES_KEY = "order_delivery_addresses";
type SavedAddress = { address: string; postcode: string };

const MULTI_CHECKOUT_STATE_KEY = "multi_checkout_state";

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }

type PiBreakdown = {
  anchorSubtotalPence: number;
  anchorDiscountPence?: number;
  anchorDiscountTitle?: string | null;
  anchorServiceChargePence: number;
  anchorBagChargePence: number;
  anchorPlatformFeePence: number;
  anchorStoreName: string;
  anchorItems: Array<{ name: string; quantity: number; lineTotalPence: number }>;
  companions: Array<{
    storeId: number;
    storeName: string;
    subtotalPence: number;
  discountPence?: number;
  discountTitle?: string | null;
    serviceChargePence: number;
    bagChargePence: number;
    platformFeePence: number;
    items: Array<{ name: string; quantity: number; lineTotalPence: number }>;
  }>;
  combinedDeliveryFeePence: number;
  separateDeliveryTotalPence: number;
  totalPlatformFeePence: number;
};

type PiData = {
  clientSecret: string;
  publishableKey: string;
  paymentIntentId: string;
  totalPence: number;
  breakdown: PiBreakdown;
};

type CheckoutState = {
  anchorStoreId: number;
  companionStoreId: number;
  anchorSlug: string;
  companionSlug: string;
  deliveryAddress: string;
  customerPostcode: string;
  customerName: string;
  customerPhone: string;
  deliveryInstructions: string;
};

// ─── Order Summary ────────────────────────────────────────────────────────────
function OrderSummary({ breakdown, totalPence }: { breakdown: PiBreakdown; totalPence: number }) {
  const [anchorOpen, setAnchorOpen] = useState(true);
  const [companionOpen, setCompanionOpen] = useState(true);
  const saving = breakdown.separateDeliveryTotalPence - breakdown.combinedDeliveryFeePence;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <ShoppingBag size={16} className="text-primary" />
        <h3 className="font-bold text-slate-800 text-sm">Order summary</h3>
      </div>

      {/* Anchor store */}
      <div className="border-b border-slate-100">
        <button
          onClick={() => setAnchorOpen(o => !o)}
          className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
        >
          <span className="font-semibold text-sm text-slate-700">{breakdown.anchorStoreName}</span>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">{pence(breakdown.anchorSubtotalPence)}</span>
            {anchorOpen ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
          </div>
        </button>
        {anchorOpen && (
          <div className="px-5 pb-3 space-y-1.5">
            {breakdown.anchorItems.map((item, i) => (
              <div key={i} className="flex justify-between text-xs text-slate-500">
                <span>{item.quantity > 1 ? `${item.quantity}× ` : ""}{item.name}</span>
                <span>{pence(item.lineTotalPence)}</span>
              </div>
            ))}
            {(breakdown.anchorDiscountPence ?? 0) > 0 && <div className="flex items-start justify-between gap-4 text-sm font-semibold text-emerald-700"><span className="min-w-0 break-words">{breakdown.anchorDiscountTitle || "Promotion"}</span><span className="shrink-0 tabular-nums">−{pence(breakdown.anchorDiscountPence!)}</span></div>}
            {breakdown.anchorServiceChargePence > 0 && (
              <div className="flex justify-between text-xs text-slate-400">
                <span>Shop service charge</span><span>{pence(breakdown.anchorServiceChargePence)}</span>
              </div>
            )}
            {breakdown.anchorBagChargePence > 0 && (
              <div className="flex justify-between text-xs text-slate-400">
                <span>Carrier bag charge</span><span>{pence(breakdown.anchorBagChargePence)}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Companion stores */}
      {breakdown.companions.map((comp, ci) => (
        <div key={comp.storeId} className="border-b border-slate-100">
          <button
            onClick={() => setCompanionOpen(o => !o)}
            className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
          >
            <span className="font-semibold text-sm text-slate-700">{comp.storeName}</span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">{pence(comp.subtotalPence)}</span>
              {ci === 0 ? (companionOpen ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />) : null}
            </div>
          </button>
          {(ci === 0 ? companionOpen : true) && (
            <div className="px-5 pb-3 space-y-1.5">
              {comp.items.map((item, i) => (
                <div key={i} className="flex justify-between text-xs text-slate-500">
                  <span>{item.quantity > 1 ? `${item.quantity}× ` : ""}{item.name}</span>
                  <span>{pence(item.lineTotalPence)}</span>
                </div>
              ))}
              {(comp.discountPence ?? 0) > 0 && <div className="flex items-start justify-between gap-4 text-sm font-semibold text-emerald-700"><span className="min-w-0 break-words">{comp.discountTitle || "Promotion"}</span><span className="shrink-0 tabular-nums">−{pence(comp.discountPence!)}</span></div>}
              {comp.serviceChargePence > 0 && (
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Shop service charge</span><span>{pence(comp.serviceChargePence)}</span>
                </div>
              )}
              {comp.bagChargePence > 0 && (
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Carrier bag charge</span><span>{pence(comp.bagChargePence)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Totals */}
      <div className="px-5 py-4 space-y-2 bg-slate-50/60">
        <div className="flex justify-between text-sm text-slate-500">
          <span>Combined delivery</span>
          <span>{breakdown.combinedDeliveryFeePence === 0 ? <span className="text-emerald-600 font-semibold">Free</span> : pence(breakdown.combinedDeliveryFeePence)}</span>
        </div>
        <div className="flex justify-between text-sm text-slate-500">
          <span>Service fee</span>
          <span>{pence(breakdown.totalPlatformFeePence)}</span>
        </div>
        {saving > 0 && (
          <div className="flex items-center gap-2 bg-emerald-50 rounded-lg px-3 py-2 mt-1">
            <Check size={14} className="text-emerald-500 flex-shrink-0" />
            <span className="text-xs text-emerald-700 font-medium">You save {pence(saving)} vs ordering separately</span>
          </div>
        )}
        <div className="flex justify-between font-bold text-slate-800 border-t border-slate-200 pt-2 mt-2">
          <span>Total</span>
          <span className="text-primary">{pence(totalPence)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Stripe Payment Form ──────────────────────────────────────────────────────
function CheckoutPaymentForm({
  totalPence, deliveryDetails, anchorStoreId, companionStoreId, anchorSlug, companionSlug,
  onPayError, saveAddressToHistory,
}: {
  totalPence: number;
  deliveryDetails: Omit<CheckoutState, "anchorStoreId" | "companionStoreId" | "anchorSlug" | "companionSlug">;
  anchorStoreId: number;
  companionStoreId: number;
  anchorSlug: string;
  companionSlug: string;
  onPayError: (msg: string) => void;
  saveAddressToHistory: (address: string, postcode: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [ready, setReady] = useState(false);
  const [paying, setPaying] = useState(false);

  async function handlePay() {
    if (!stripe || !elements || paying) return;
    setPaying(true);

    try {
      // Save checkout state before charging so the Stripe redirect can be restored.
      sessionStorage.setItem(MULTI_CHECKOUT_STATE_KEY, JSON.stringify({
        anchorStoreId, companionStoreId, anchorSlug, companionSlug, ...deliveryDetails,
      } satisfies CheckoutState));
      saveAddressToHistory(deliveryDetails.deliveryAddress, deliveryDetails.customerPostcode);

      const returnUrl = new URL(window.location.origin + "/order/multi-checkout");
      returnUrl.searchParams.set("anchorStoreId", String(anchorStoreId));
      returnUrl.searchParams.set("companionStoreId", String(companionStoreId));
      returnUrl.searchParams.set("anchorSlug", anchorSlug);
      returnUrl.searchParams.set("companionSlug", companionSlug);

      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl.toString() },
      });

      if (error) {
        onPayError(error.message ?? "Payment was not completed. Please try again.");
      }
    } catch {
      onPayError("Could not start payment. Please try again.");
    } finally {
      setPaying(false);
    }
  }

  return (
    <div className="space-y-4">
      <PaymentElement onReady={() => setReady(true)} options={{ layout: "tabs" }} />
      <Button
        className="w-full bg-primary hover:bg-primary/90 font-bold text-base py-6"
        disabled={!ready || !stripe || !elements || paying}
        onClick={handlePay}
      >
        {paying ? (
          <><Loader2 size={16} className="animate-spin mr-2" />Processing…</>
        ) : (
          `Pay ${pence(totalPence)}`
        )}
      </Button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function MultiOrderCheckout() {
  const [, navigate] = useLocation();
  const { user, loginWithToken } = useAuth();
  const { toast } = useToast();

  const urlParams = new URLSearchParams(window.location.search);
  const anchorStoreId = Number(urlParams.get("anchorStoreId"));
  const companionStoreId = Number(urlParams.get("companionStoreId"));
  const anchorSlug = urlParams.get("anchorSlug") ?? "";
  const companionSlug = urlParams.get("companionSlug") ?? "";

  // Stripe redirect params (present after payment attempt)
  const paymentIntentFromUrl = urlParams.get("payment_intent");
  const redirectStatus = urlParams.get("redirect_status");

  const isRedirectReturn = !!(paymentIntentFromUrl && redirectStatus);
  const [mode, setMode] = useState<"form" | "verifying" | "verified" | "error">(
    isRedirectReturn ? "verifying" : "form"
  );
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const { deliveryPostcode } = useTown();
  // Form fields
  const [address, setAddress] = useState("");
  const [postcode, setPostcode] = useState(deliveryPostcode ?? "");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [showSavedAddresses, setShowSavedAddresses] = useState(false);

  // PI state
  const [piData, setPiData] = useState<PiData | null>(null);
  const [piLoading, setPiLoading] = useState(false);
  const [piError, setPiError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stripePromise = useMemo(
    () => (piData?.publishableKey ? loadStripe(piData.publishableKey) : null),
    [piData?.publishableKey]
  );

  // ── Load saved addresses + pre-fill name/phone from profile ─────────────
  useEffect(() => {
    try {
      const raw = localStorage.getItem(ADDRESSES_KEY);
      if (raw) setSavedAddresses(JSON.parse(raw) as SavedAddress[]);
    } catch {}
    // Pre-fill name/phone from user profile if fields are empty
    if (user?.name) setName(current => current || user.name);
    if (user?.phone) setPhone(current => current || user.phone || "");
  }, [user]);

  function saveAddressToHistory(addr: string, pc: string) {
    if (!addr.trim() && !pc.trim()) return;
    const entry: SavedAddress = { address: addr.trim(), postcode: pc.trim() };
    try {
      const raw = localStorage.getItem(ADDRESSES_KEY);
      const prev: SavedAddress[] = raw ? (JSON.parse(raw) as SavedAddress[]) : [];
      const deduped = prev.filter((s) => !(s.address === entry.address && s.postcode === entry.postcode));
      const next = [entry, ...deduped].slice(0, 3);
      localStorage.setItem(ADDRESSES_KEY, JSON.stringify(next));
      setSavedAddresses(next);
    } catch {}
  }

  // ── Verify payment after Stripe redirect ─────────────────────────────────
  useEffect(() => {
    if (!isRedirectReturn) return;
    void runVerify();
  }, []);

  async function ensureAuth(): Promise<string | null> {
    let token = localStorage.getItem("ayrshire_token");
    if (!token) {
      try {
        const res = await fetch("/api/auth/guest", { method: "POST", headers: { "Content-Type": "application/json" } });
        if (!res.ok) return null;
        const d = await res.json() as { token: string };
        token = d.token;
        localStorage.setItem("ayrshire_token", token);
        localStorage.setItem("ayrshire_is_guest", "1");
        void loginWithToken(token);
      } catch { return null; }
    }
    return token;
  }

  async function runVerify() {
    if (redirectStatus !== "succeeded") {
      setMode("error");
      setVerifyError(
        redirectStatus === "processing"
          ? "Payment is still processing — please wait a moment then refresh."
          : "Payment was not completed. Please go back and try again."
      );
      return;
    }

    try {
      const savedRaw = sessionStorage.getItem(MULTI_CHECKOUT_STATE_KEY);
      const state: CheckoutState | null = savedRaw ? JSON.parse(savedRaw) as CheckoutState : null;

      if (!state) {
        setMode("error");
        setVerifyError("Checkout session expired. Your payment may have been taken — please contact support with reference: " + (paymentIntentFromUrl ?? "unknown"));
        return;
      }

      const token = await ensureAuth();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch("/api/multi-order/checkout-verify", {
        method: "POST",
        headers,
        body: JSON.stringify({
          paymentIntentId: paymentIntentFromUrl,
          anchorStoreId: state.anchorStoreId,
          companionStoreIds: [state.companionStoreId],
          deliveryAddress: state.deliveryAddress,
          customerPostcode: state.customerPostcode || undefined,
          customerName: state.customerName || undefined,
          customerPhone: state.customerPhone || undefined,
          deliveryInstructions: state.deliveryInstructions || undefined,
        }),
      });

      const d = await res.json() as { multiOrder?: { id: number }; error?: string; alreadyCreated?: boolean };

      if (d.multiOrder) {
        setMode("verified");
        sessionStorage.removeItem(MULTI_CHECKOUT_STATE_KEY);
        try { localStorage.removeItem(`order_cart_${state.anchorSlug}`); } catch {}
        try { localStorage.removeItem(`order_cart_${state.companionSlug}`); } catch {}
        navigate(`/order/multi-track/${d.multiOrder.id}`);
      } else {
        setMode("error");
        setVerifyError(
          (d.error ?? "Could not confirm your order.") +
          " Your payment reference is: " + (paymentIntentFromUrl ?? "unknown") + ". Please contact support."
        );
      }
    } catch {
      setMode("error");
      setVerifyError(
        "Network error confirming your order. Payment reference: " + (paymentIntentFromUrl ?? "unknown") + ". Please contact support."
      );
    }
  }

  // ── Fetch PI whenever address changes ────────────────────────────────────
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!address.trim() || address.trim().length < 5) {
      setPiData(null);
      setPiError(null);
      return;
    }
    debounceRef.current = setTimeout(() => void fetchPi(), 700);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [address]);

  async function fetchPi() {
    setPiLoading(true);
    setPiError(null);
    try {
      const token = await ensureAuth();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      // Sync companion cart to server (it may not be synced yet)
      const companionCartRaw = (() => {
        try { return localStorage.getItem(`order_cart_${companionSlug}`); } catch { return null; }
      })();
      if (companionCartRaw) {
        const items = JSON.parse(companionCartRaw) as object[];
        if (items.length > 0) {
          await fetch("/api/order/basket", {
            method: "PUT",
            headers,
            body: JSON.stringify({ storeId: companionStoreId, items }),
          });
        }
      }

      // Sync anchor cart too (in case session changed)
      const anchorCartRaw = (() => {
        try { return localStorage.getItem(`order_cart_${anchorSlug}`); } catch { return null; }
      })();
      if (anchorCartRaw) {
        const items = JSON.parse(anchorCartRaw) as object[];
        if (items.length > 0) {
          await fetch("/api/order/basket", {
            method: "PUT",
            headers,
            body: JSON.stringify({ storeId: anchorStoreId, items }),
          });
        }
      }

      const pc = postcode.trim() || extractPostcode(address);
      const deliveryAddress = buildAddress(address, postcode);

      const res = await fetch("/api/multi-order/payment-intent", {
        method: "POST",
        headers,
        body: JSON.stringify({
          anchorStoreId,
          companionStoreIds: [companionStoreId],
          deliveryAddress,
          customerPostcode: pc || undefined,
          customerName: name.trim() || undefined,
          customerPhone: phone.trim() || undefined,
          deliveryInstructions: notes.trim() || undefined,
        }),
      });

      const d = await res.json() as (PiData & { error?: string });
      if ("error" in d && d.error) {
        setPiError(d.error as string);
        setPiData(null);
      } else {
        setPiData(d as PiData);
        setPayError(null);
      }
    } catch {
      setPiError("Could not calculate order total. Please check your connection.");
    } finally {
      setPiLoading(false);
    }
  }

  function extractPostcode(addr: string): string {
    const m = addr.match(/[A-Z]{1,2}[0-9][0-9A-Z]?\s*[0-9][A-Z]{2}/i);
    return m ? m[0].toUpperCase() : "";
  }

  function buildAddress(addr: string, pc: string): string {
    const a = addr.trim();
    const p = pc.trim();
    if (!a) return p;
    if (p && !a.toLowerCase().includes(p.toLowerCase())) return `${a}, ${p}`;
    return a;
  }

  // ── Loading / verifying screens ──────────────────────────────────────────
  if (mode === "verifying" || mode === "verified") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={36} className="animate-spin text-primary mx-auto mb-4" />
          <p className="font-semibold text-slate-700">Confirming your order…</p>
          <p className="text-sm text-slate-400 mt-1">Please don't close this page</p>
        </div>
      </div>
    );
  }

  if (mode === "error") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-[14px] border border-red-200 p-8 text-center shadow-sm">
          <AlertTriangle size={40} className="mx-auto text-red-500 mb-4" />
          <h2 className="font-bold text-slate-800 mb-2">Something went wrong</h2>
          <p className="text-sm text-slate-600 mb-6">{verifyError}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate(`/order/${anchorSlug}`)}
              className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50"
            >
              Back to stores
            </button>
            <button
              onClick={() => { setMode("form"); setVerifyError(null); }}
              className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const deliveryAddress = buildAddress(address, postcode);
  const deliveryDetails = {
    deliveryAddress,
    customerPostcode: postcode.trim() || extractPostcode(address),
    customerName: name.trim(),
    customerPhone: phone.trim(),
    deliveryInstructions: notes.trim(),
  };

  const canPay = !!piData && !!name.trim() && !!phone.trim() && address.trim().length >= 5;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(`/order/${companionSlug}?multiAnchorStoreId=${anchorStoreId}&anchorSlug=${anchorSlug}`)}
            className="p-2 rounded-full hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-[28px] leading-tight font-bold tracking-[-1px] text-[#1e3a5f]">Combined checkout</h1>
            <p className="text-xs text-slate-500">Card payment only · Delivery only</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-8 space-y-5">
        {/* Delivery details */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <h2 className="font-bold text-slate-800 text-sm">Delivery details</h2>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-slate-500">Delivery address *</label>
              {savedAddresses.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowSavedAddresses(v => !v)}
                  className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors"
                >
                  <Clock size={11} />
                  Previous
                  {showSavedAddresses ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                </button>
              )}
            </div>
            {showSavedAddresses && savedAddresses.length > 0 && (
              <div className="rounded-lg border border-slate-200 overflow-hidden shadow-sm">
                {savedAddresses.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setAddress(s.address); setPostcode(s.postcode); setShowSavedAddresses(false); }}
                    className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors ${i < savedAddresses.length - 1 ? "border-b border-slate-100" : ""}`}
                  >
                    <p className="text-sm font-medium text-slate-800 truncate">{s.address || s.postcode}</p>
                    {s.postcode && s.address && <p className="text-xs text-slate-400 mt-0.5">{s.postcode}</p>}
                  </button>
                ))}
              </div>
            )}
            <AddressAutocomplete
              value={address}
              onSelect={(addr, pc) => { setAddress(addr); setPostcode(pc); }}
              placeholder="Start typing your address…"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-500">Your name *</label>
              <Input
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setNameTouched(true)}
                className={nameTouched && !name.trim() ? "border-red-300 focus-visible:ring-red-300" : ""}
                autoComplete="name"
              />
              {nameTouched && !name.trim() && <p className="text-xs text-red-500">Required</p>}
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-500">Phone number *</label>
              <Input
                placeholder="Mobile number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onBlur={() => setPhoneTouched(true)}
                className={phoneTouched && !phone.trim() ? "border-red-300 focus-visible:ring-red-300" : ""}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
              />
              {phoneTouched && !phone.trim() && <p className="text-xs text-red-500">Required</p>}
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-500">Delivery instructions (optional)</label>
            <textarea
              placeholder="Leave at door, ring bell…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
            />
          </div>
        </div>

        {/* PI loading / error */}
        {address.trim().length >= 5 && piLoading && (
          <div className="flex items-center gap-2 text-sm text-slate-400 px-1">
            <Loader2 size={14} className="animate-spin" />Calculating your total…
          </div>
        )}
        {piError && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {piError}
          </div>
        )}

        {/* Order summary */}
        {piData && <OrderSummary breakdown={piData.breakdown} totalPence={piData.totalPence} />}

        {/* Stripe payment */}
        {piData && stripePromise && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <h2 className="font-bold text-slate-800 text-sm">Payment</h2>
            {payError && (
              <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {payError}
              </div>
            )}
            {(!name.trim() || !phone.trim()) && (
              <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                Please fill in your name and phone number above before paying.
              </p>
            )}
            <Elements
              key={piData.clientSecret}
              stripe={stripePromise}
              options={{ clientSecret: piData.clientSecret, appearance: orderingPaymentAppearance }}
            >
              <CheckoutPaymentForm
                totalPence={piData.totalPence}
                deliveryDetails={deliveryDetails}
                anchorStoreId={anchorStoreId}
                companionStoreId={companionStoreId}
                anchorSlug={anchorSlug}
                companionSlug={companionSlug}
                onPayError={(msg) => setPayError(msg)}
                saveAddressToHistory={saveAddressToHistory}
              />
            </Elements>
          </div>
        )}

        {/* Placeholder pay button before PI is ready */}
        {!piData && !piLoading && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-sm text-slate-400 text-center py-4">
              {address.trim().length < 5
                ? "Enter your delivery address to see payment options"
                : piError
                ? "Fix the error above to continue"
                : "Loading payment…"}
            </p>
          </div>
        )}

        <p className="text-xs text-center text-slate-400">
          Payments are processed securely by Stripe. You'll receive order updates by SMS.
        </p>
      </div>
    </div>
  );
}
